import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../contexts/UserContext";
import Sidebar from "../common/Sidebar";
import MobileTopbar from "../common/MobileTopbar";
import MobileSidebarDrawer from "../common/MobileSidebarDrawer";

const NewHomeComponent2 = () => {
  const { userData } = useContext(UserContext);
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState("Mathematics");

  const subjects = ["Mathematics", "Physics", "Chemistry", "Biology"];

  // Sample mission data - in real app, this would come from API/context
  const missionData = {
    title: "Linear Equations",
    subtitle: "My active mission",
    progress: 75,
    mastery: 75,
  };

  const handleCheckWork = () => {
    navigate("/practice/evaluateAnswerSheet");
  };

  // Shield icons with different states
  const ShieldIcon = ({ type, position, progress }) => {
    const threshold = (position - 1) * 25;
    const isUnlocked = progress >= threshold;
    const isPartiallyUnlocked =
      progress > threshold && progress < threshold + 25;

    let fillColor = "#E5E7EB"; // Grey - locked
    let iconColor = "#9CA3AF";
    let strokeColor = "#D1D5DB";

    if (type === "first") {
      fillColor = isUnlocked ? "#F97316" : "#E5E7EB"; // Orange when unlocked
      iconColor = isUnlocked ? "#FFFFFF" : "#9CA3AF";
      strokeColor = isUnlocked ? "#FFFFFF" : "#D1D5DB";
    } else if (type === "second") {
      fillColor = isUnlocked ? "#6B7280" : "#E5E7EB"; // Grey with stars when unlocked
      iconColor = isUnlocked ? "#FCD34D" : "#9CA3AF"; // Yellow stars
      strokeColor = isUnlocked ? "#FFFFFF" : "#D1D5DB";
    } else if (type === "third") {
      fillColor = isUnlocked ? "#6B7280" : "#E5E7EB"; // Grey with airplane when unlocked
      iconColor = isUnlocked ? "#FFFFFF" : "#9CA3AF";
      strokeColor = isUnlocked ? "#FFFFFF" : "#D1D5DB";
    } else if (type === "fourth") {
      fillColor = isUnlocked ? "#FCD34D" : "#E5E7EB"; // Yellow when unlocked
      iconColor = isUnlocked ? "#FFFFFF" : "#9CA3AF";
      strokeColor = isUnlocked ? "#FFFFFF" : "#D1D5DB";
    }

    // Position shields at 0%, 25%, 50%, and 100%
    const positions = [0, 25, 50, 100];
    const leftPosition = positions[position - 1];

    return (
      <div
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-10"
        style={{ left: `${leftPosition}%` }}
      >
        <svg
          width="36"
          height="36"
          viewBox="0 0 36 36"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-8 h-8 md:w-9 md:h-9"
        >
          {/* Shield shape */}
          <path
            d="M18 5L8 9V18C8 24 12 28 18 30C24 28 28 24 28 18V9L18 5Z"
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="1.5"
          />
          {/* Icon content based on type */}
          {type === "first" && isUnlocked && (
            <circle cx="18" cy="18" r="4" fill={iconColor} />
          )}
          {type === "second" && isUnlocked && (
            <>
              <path
                d="M18 13L19.5 16.5L23 17L20 19.5L20.5 23L18 21.5L15.5 23L16 19.5L13 17L16.5 16.5L18 13Z"
                fill={iconColor}
              />
              <circle cx="11" cy="11" r="1.5" fill={iconColor} />
              <circle cx="25" cy="11" r="1.5" fill={iconColor} />
            </>
          )}
          {type === "third" && isUnlocked && (
            <>
              <path
                d="M9 18L18 13L27 18L22 21L18 19L14 21L9 18Z"
                fill={iconColor}
              />
              <circle cx="11" cy="11" r="1.5" fill={iconColor} />
              <circle cx="25" cy="11" r="1.5" fill={iconColor} />
            </>
          )}
          {type === "fourth" && isUnlocked && (
            <path
              d="M18 11L20 15L24 16L21 19L21.5 23L18 21L14.5 23L15 19L12 16L16 15L18 11Z"
              fill={iconColor}
            />
          )}
        </svg>
      </div>
    );
  };

  return (
    <div className="h-[100dvh] flex overflow-hidden bg-white">
      {/* Sidebar - shown only on md+ screens */}
      <div className="hidden md:block">
        <Sidebar />
      </div>

      {/* Mobile Drawer */}
      <MobileSidebarDrawer
        open={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Topbar */}
        <MobileTopbar
          onMenuClick={() => setIsMobileMenuOpen(true)}
          showMenuButton={true}
          showProfileButton={false}
          showEditButton={false}
        />

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto px-4 md:px-8 py-6 md:py-12">
          <div className="max-w-4xl mx-auto">
            {/* Subject Navigation Tabs */}
            <div className="mb-6 md:mb-8">
              <div className="flex gap-2 md:gap-3 overflow-x-auto pb-2 -mx-2 px-2 md:mx-0 md:px-0">
                {subjects.map((subject) => (
                  <button
                    key={subject}
                    onClick={() => setSelectedSubject(subject)}
                    className={`px-4 md:px-6 py-2 md:py-3 rounded-full text-sm md:text-base font-medium whitespace-nowrap transition-all duration-200 flex-shrink-0 ${
                      selectedSubject === subject
                        ? "bg-[#0D99FF] text-white shadow-md"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {subject}
                  </button>
                ))}
              </div>
            </div>

            {/* Mission Card */}
            <div className="bg-white rounded-2xl md:rounded-3xl shadow-lg border border-gray-100 p-6 md:p-8 lg:p-10">
              {/* Title and Subtitle */}
              <div className="text-center mb-6 md:mb-8">
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                  {missionData.title}
                </h2>
                <p className="text-sm md:text-base text-gray-500">
                  {missionData.subtitle}
                </p>
              </div>

              {/* Progress Bar Container */}
              <div className="relative mb-8 md:mb-10">
                {/* Background Progress Bar */}
                <div className="relative h-8 md:h-10 bg-gray-200 rounded-full overflow-hidden">
                  {/* Filled Progress */}
                  <div
                    className="absolute top-0 left-0 h-full bg-[#0D99FF] rounded-full transition-all duration-500"
                    style={{ width: `${missionData.progress}%` }}
                  />
                  {/* Progress Text - positioned on the blue part */}
                  <div
                    className="absolute top-0 left-0 h-full flex items-center justify-center"
                    style={{ width: `${missionData.progress}%` }}
                  >
                    <span className="text-xs md:text-sm font-semibold text-white z-10">
                      {missionData.mastery}% Mastery
                    </span>
                  </div>
                </div>

                {/* Shield Icons - positioned along the progress bar */}
                <div className="relative -mt-5 md:-mt-6">
                  <ShieldIcon
                    type="first"
                    position={1}
                    progress={missionData.progress}
                  />
                  <ShieldIcon
                    type="second"
                    position={2}
                    progress={missionData.progress}
                  />
                  <ShieldIcon
                    type="third"
                    position={3}
                    progress={missionData.progress}
                  />
                  <ShieldIcon
                    type="fourth"
                    position={4}
                    progress={missionData.progress}
                  />
                </div>
              </div>

              {/* Check My Work Button */}
              <div className="flex justify-center">
                <button
                  onClick={handleCheckWork}
                  className="w-full md:w-auto px-8 md:px-12 py-3 md:py-4 bg-[#0D99FF] text-white rounded-xl md:rounded-2xl text-base md:text-lg font-semibold hover:bg-[#6351E0] transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5"
                >
                  Check My Work
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewHomeComponent2;
